import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, formatPrice, formatMileage, T } from './UI';

/* ── Drawn car illustration — profile silhouette in SVG ── */
const BODY_PATHS = {
  SUV:    'M8,42 L8,30 Q10,18 22,16 L48,14 Q62,12 72,18 L84,22 Q92,24 94,30 L94,42 Z M14,42 A7,7 0 1,0 28,42 A7,7 0 1,0 14,42 M72,42 A7,7 0 1,0 86,42 A7,7 0 1,0 72,42',
  Saloon: 'M6,42 L6,32 Q8,22 20,20 L36,14 Q52,10 66,14 L82,20 Q92,24 95,32 L95,42 Z M13,42 A7,7 0 1,0 27,42 A7,7 0 1,0 13,42 M73,42 A7,7 0 1,0 87,42 A7,7 0 1,0 73,42',
  Hatchback:'M8,42 L8,32 Q10,22 22,18 L44,12 Q58,10 68,16 L82,22 Q92,26 94,34 L94,42 Z M14,42 A7,7 0 1,0 28,42 A7,7 0 1,0 14,42 M70,42 A7,7 0 1,0 84,42 A7,7 0 1,0 70,42',
  Estate:  'M5,42 L5,32 Q7,20 20,18 L40,12 Q62,10 78,14 L90,22 Q97,26 97,34 L97,42 Z M12,42 A7,7 0 1,0 26,42 A7,7 0 1,0 12,42 M74,42 A7,7 0 1,0 88,42 A7,7 0 1,0 74,42',
  Coupe:   'M10,42 L10,34 Q12,22 26,16 L50,10 Q66,8 76,16 L88,26 Q94,30 94,38 L94,42 Z M16,42 A7,7 0 1,0 30,42 A7,7 0 1,0 16,42 M70,42 A7,7 0 1,0 84,42 A7,7 0 1,0 70,42',
  default: 'M8,42 L8,30 Q10,20 22,16 L46,12 Q62,10 72,16 L84,22 Q93,26 94,34 L94,42 Z M14,42 A7,7 0 1,0 28,42 A7,7 0 1,0 14,42 M70,42 A7,7 0 1,0 84,42 A7,7 0 1,0 70,42',
};

const COLOUR_MAP = {
  Black: '#1a1916', White: '#f5f3ef', Silver: '#a8a8a8', Grey: '#787878',
  Blue: '#1e3a5f', Red: '#8b1c1c', Green: '#1a3a1a', Orange: '#b45309',
  Yellow: '#b45309', Brown: '#5c3d1e', Beige: '#c8b89a',
};

export function CarIllustration({ bodyType = 'default', colour = 'Silver', size = 100 }) {
  const path = BODY_PATHS[bodyType] || BODY_PATHS.default;
  const bodyFill  = COLOUR_MAP[colour] || '#888';
  const isLight   = ['White','Silver','Beige','Yellow'].includes(colour);
  const windowFill= isLight ? 'rgba(0,0,0,0.35)' : 'rgba(255,255,255,0.18)';
  const rimFill   = isLight ? '#555' : '#d4d0c8';
  return (
    <svg viewBox="0 0 102 56" width={size} height={size * 56/102} role="img" aria-label={`${colour} ${bodyType}`} style={{ display: 'block' }}>
      <path d={path} fill={bodyFill} />
      {/* windscreen + side windows approximation */}
      <ellipse cx="38" cy="24" rx="10" ry="7" fill={windowFill} opacity="0.9"/>
      <ellipse cx="62" cy="24" rx="9" ry="6" fill={windowFill} opacity="0.7"/>
      {/* headlight */}
      <ellipse cx="90" cy="36" rx="4" ry="3" fill="#e8e0c0" opacity="0.9"/>
      {/* taillight */}
      <ellipse cx="10" cy="36" rx="3" ry="2.5" fill="#c04040" opacity="0.8"/>
      {/* wheel rims */}
      <circle cx="21" cy="42" r="4" fill={rimFill} />
      <circle cx="79" cy="42" r="4" fill={rimFill} />
    </svg>
  );
}

/* ── Hero car illustration — larger, more detailed ── */
export function HeroCarIllustration({ bodyType = 'SUV', colour = 'Black' }) {
  const bodyFill  = COLOUR_MAP[colour] || '#1a1916';
  const isLight   = ['White','Silver','Beige','Yellow'].includes(colour);
  const glassC    = isLight ? 'rgba(20,40,80,0.45)' : 'rgba(120,160,220,0.35)';
  const rimC      = isLight ? '#555' : '#c8c4bc';
  return (
    <svg viewBox="0 0 320 180" width="100%" role="img" aria-label={`${colour} ${bodyType} car illustration`} style={{ display: 'block', maxWidth: 480 }}>
      {/* body */}
      <path d="M20,135 L20,95 Q24,58 60,48 L130,32 Q180,22 220,36 L275,56 Q302,68 308,95 L308,135 Z" fill={bodyFill}/>
      {/* roof highlight */}
      <path d="M60,48 L130,32 Q180,22 220,36 L240,44 Q190,28 130,38 L65,54 Z" fill="rgba(255,255,255,0.07)"/>
      {/* windscreen */}
      <path d="M105,50 L80,56 Q68,60 64,76 L72,80 L120,60 Z" fill={glassC}/>
      {/* side windows */}
      <path d="M125,46 L188,38 L195,56 L120,60 Z" fill={glassC}/>
      <path d="M193,38 L228,42 Q240,46 244,58 L196,58 Z" fill={glassC}/>
      {/* door seam */}
      <line x1="175" y1="56" x2="175" y2="120" stroke="rgba(0,0,0,0.2)" strokeWidth="1.5"/>
      {/* headlight cluster */}
      <ellipse cx="292" cy="108" rx="12" ry="8" fill="#e0d8b4" opacity="0.9"/>
      <ellipse cx="294" cy="107" rx="6" ry="4" fill="#f0e8c0"/>
      {/* taillight */}
      <rect x="20" y="100" width="6" height="22" rx="1" fill="#c04040" opacity="0.85"/>
      {/* wheels */}
      <circle cx="82"  cy="135" r="22" fill="#222"/>
      <circle cx="82"  cy="135" r="16" fill={rimC}/>
      <circle cx="82"  cy="135" r="8"  fill="#333"/>
      {[0,60,120,180,240,300].map(a => (
        <line key={a} x1="82" y1="135" x2={82+14*Math.cos(a*Math.PI/180)} y2={135+14*Math.sin(a*Math.PI/180)} stroke="#444" strokeWidth="2"/>
      ))}
      <circle cx="238" cy="135" r="22" fill="#222"/>
      <circle cx="238" cy="135" r="16" fill={rimC}/>
      <circle cx="238" cy="135" r="8"  fill="#333"/>
      {[0,60,120,180,240,300].map(a => (
        <line key={a} x1="238" y1="135" x2={238+14*Math.cos(a*Math.PI/180)} y2={135+14*Math.sin(a*Math.PI/180)} stroke="#444" strokeWidth="2"/>
      ))}
      {/* ground shadow */}
      <ellipse cx="160" cy="158" rx="130" ry="7" fill="rgba(0,0,0,0.15)"/>
    </svg>
  );
}

/* ── Card photo panel — atmosphere image + illustration overlay ── */
const CARD_IMAGES = [
  'https://picsum.photos/id/187/800/500',  /* frost field — pale gold tones */
  'https://picsum.photos/id/112/800/500',  /* golden grass */
  'https://picsum.photos/id/955/800/500',  /* aerial crop rows */
  'https://picsum.photos/id/85/800/500',   /* golden field */
  'https://picsum.photos/id/107/800/500',  /* green wheat */
  'https://picsum.photos/id/699/800/500',  /* rolling hills */
];
function cardImg(id) {
  const hash = id ? id.split('').reduce((a,c) => a + c.charCodeAt(0), 0) : 0;
  return CARD_IMAGES[hash % CARD_IMAGES.length];
}

export default function CarCard({ car }) {
  const nav = useNavigate();
  const isNew = (Date.now() - new Date(car.publishedAt).getTime()) < 7 * 24 * 60 * 60 * 1000;

  return (
    <article
      onClick={() => nav(`/car/${car.id}`)}
      style={{ background: T.bg, cursor: 'pointer', border: `1px solid ${T.surface}`, transition: 'border-color 0.2s, transform 0.2s', display: 'flex', flexDirection: 'column' }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = T.accent; e.currentTarget.style.transform = 'translateY(-2px)'; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = T.surface; e.currentTarget.style.transform = 'none'; }}>

      {/* Image panel */}
      <div style={{ position: 'relative', aspectRatio: '16/9', overflow: 'hidden', background: T.ink }}>
        {/* Atmosphere photo */}
        <img
          src={cardImg(car.id)}
          alt=""
          loading="lazy"
          width="800" height="500"
          style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: 0.55, display: 'block' }}
        />
        {/* Car illustration centred */}
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', paddingBottom: 8 }}>
          <CarIllustration bodyType={car.bodyType} colour={car.colour} size={160} />
        </div>
        {/* UK strip */}
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: 'linear-gradient(90deg,#012169 33%,#fff 33%,#fff 34%,#C8102E 34%,#C8102E 66%,#fff 66%,#fff 67%,#012169 67%)' }} />
        {/* Badges */}
        <div style={{ position: 'absolute', top: 10, left: 10, display: 'flex', gap: 4 }}>
          {isNew && car.status === 'available' && <Badge variant="gold">Just In</Badge>}
          {car.status !== 'available' && <Badge variant="sold" style={{ background: T.ink }}>{car.status === 'sold' ? 'Sold' : 'Reserved'}</Badge>}
        </div>
        {/* Views */}
        <div style={{ position: 'absolute', bottom: 10, right: 10, color: 'rgba(255,255,255,0.65)', fontSize: 'var(--step-xs)', letterSpacing: '0.04em' }}>
          {car.views} views
        </div>
      </div>

      {/* Body */}
      <div style={{ padding: '18px 20px 20px', flex: 1, display: 'flex', flexDirection: 'column' }}>
        <p style={{ fontSize: 'var(--step-xs)', color: T.muted, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 6 }}>
          {car.fuelType} · {car.transmission}
        </p>
        <h3 style={{ fontSize: 'var(--step-lead)', fontFamily: 'Playfair Display, serif', fontWeight: 400, color: T.ink, lineHeight: 1.2, marginBottom: 12 }}>
          {car.year} {car.make} {car.model}
        </h3>
        <div style={{ display: 'flex', gap: 20, marginBottom: 16, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 'var(--step-xs)', color: T.muted }}>{formatMileage(car.mileage)}</span>
          <span style={{ fontSize: 'var(--step-xs)', color: T.muted }}>{car.colour}</span>
          <span style={{ fontSize: 'var(--step-xs)', color: T.muted }}>{car.engine}</span>
        </div>
        <div style={{ marginTop: 'auto', paddingTop: 14, borderTop: `1px solid ${T.surface}`, display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <span style={{ fontSize: 'var(--step-head)', fontFamily: 'Playfair Display, serif', fontWeight: 400, color: T.ink, lineHeight: 1 }}>{formatPrice(car.price)}</span>
          {car.monthlyPrice > 0 && <span style={{ fontSize: 'var(--step-xs)', color: T.muted }}>from {formatPrice(car.monthlyPrice)}/mo</span>}
        </div>
      </div>
    </article>
  );
}
